import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { ReportTypeDefinition } from '../../../dto/advi/advi.dto';
import { REPORT_TYPE_DEFINITIONS } from './report-type-registry';

export interface FilterModuleItem {
  module_id: number;
  name: string;
  required: boolean;
}

export interface FilterUserItem {
  id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
}

/** Max parallel /paths/{guid}/versions requests (backend builds each response with many queries). */
export const PATH_VERSIONS_REQUEST_CONCURRENCY = 3;

export interface GenerateReportResponse {
  success: boolean;
  pdf_content?: string;
  filename?: string;
  data?: unknown;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class ReportWizardApiService {
  private base = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getReportTypes(): Observable<ReportTypeDefinition[]> {
    return this.http.get<{ success: boolean; types: ReportTypeDefinition[] }>(`${this.base}/reports/types`).pipe(
      map(r => (r.types?.length ? r.types : REPORT_TYPE_DEFINITIONS)),
      catchError(err => {
        console.warn('[ReportWizardApiService] GET /reports/types failed, using client registry', err);
        return of(REPORT_TYPE_DEFINITIONS);
      }),
    );
  }

  getFilterModules(
    pathIds: number[],
    pathVersionMode?: 'grouped' | 'separated',
  ): Observable<FilterModuleItem[]> {
    let params = new HttpParams();
    if (pathIds.length > 0) {
      params = params.set('path_ids', pathIds.join(','));
    } else {
      params = params.set('path_ids', 'all');
    }
    if (pathVersionMode === 'separated') {
      params = params.set('path_version_mode', 'separated');
    }
    return this.http.get<any>(`${this.base}/reports/filter-options/modules`, { params }).pipe(
      map(r => this.extractItems<FilterModuleItem>(r)),
    );
  }

  /**
   * Fallback: vedligeholdelsesmoduler fra alle versioner på valgte uddannelsesveje.
   * Ingen års-filter — alle vedligeholdelsesrækker med module_id inkluderes.
   */
  loadModulesForPaths(paths: { guid: string }[]): Observable<FilterModuleItem[]> {
    if (!paths.length) return of([]);

    const listParams = new HttpParams().set('pageSize', '10000').set('page', '1');

    // Bounded concurrency (was an unrestricted forkJoin over every path). Results are
    // re-ordered to the original path order so dedupeModules() output is unchanged.
    return from(paths.map((p, index) => ({ p, index }))).pipe(
      mergeMap(
        ({ p, index }) =>
          this.http.get<any>(`${this.base}/paths/${p.guid}/versions`).pipe(
            map(resp => this.extractListRows(resp)),
            catchError(() => of([])),
            switchMap((versions: any[]) => {
              const versionIds = [...new Set(
                versions.map(v => Number(v.version_id)).filter(id => id > 0),
              )];
              if (!versionIds.length) return of([] as FilterModuleItem[]);

              return forkJoin(
                versionIds.map(versionId =>
                  this.http.get<any>(`${this.base}/paths/versions/${versionId}/maintenance`, { params: listParams }).pipe(
                    map(resp => this.mapMaintenanceRows(resp)),
                    catchError(() => of([] as FilterModuleItem[])),
                  ),
                ),
              ).pipe(map(groups => groups.flat()));
            }),
            map(items => ({ index, items })),
          ),
        PATH_VERSIONS_REQUEST_CONCURRENCY,
      ),
      toArray(),
      map(results =>
        this.dedupeModules(
          results.sort((a, b) => a.index - b.index).flatMap(r => r.items),
        ),
      ),
    );
  }

  getFilterUsers(
    locationGuids: string[],
    search?: string,
    groupIds?: number[],
  ): Observable<FilterUserItem[]> {
    let params = new HttpParams();
    if (locationGuids.length > 0) {
      params = params.set('location_guids', locationGuids.join(','));
    }
    if (groupIds && groupIds.length > 0) {
      params = params.set('group_ids', groupIds.join(','));
    }
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<any>(`${this.base}/reports/filter-options/users`, { params }).pipe(
      map(r => this.extractItems<FilterUserItem>(r)),
    );
  }

  getUsersManage(search?: string): Observable<FilterUserItem[]> {
    let params = new HttpParams().set('pageSize', '10000').set('page', '1');
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<any>(`${this.base}/users/manage`, { params }).pipe(
      map(r => this.mapUserRows(r)),
      catchError(err => {
        console.warn('[ReportWizardApiService] GET /users/manage failed', err);
        return of([]);
      }),
    );
  }

  getLocationUsers(locationGuid: string, search?: string): Observable<FilterUserItem[]> {
    let params = new HttpParams()
      .set('location_guid', locationGuid)
      .set('pageSize', '10000')
      .set('page', '1');
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<any>(`${this.base}/locations/users`, { params }).pipe(
      map(r => this.mapUserRows(r)),
      catchError(err => {
        console.warn('[ReportWizardApiService] GET /locations/users failed', err);
        return of([]);
      }),
    );
  }

  loadUsersFallback(locationGuids: string[], search?: string): Observable<FilterUserItem[]> {
    if (locationGuids.length === 0) {
      return this.getUsersManage(search);
    }
    if (locationGuids.length === 1) {
      return this.getLocationUsers(locationGuids[0], search);
    }
    return forkJoin(locationGuids.map(g => this.getLocationUsers(g, search))).pipe(
      map(groups => this.dedupeUsers(groups.flat())),
    );
  }

  /**
   * Number of versions for a path, or null when the request failed.
   * Unlike getPathVersions(), errors are NOT collapsed to an empty list, so callers can
   * tell "no multiple versions" apart from "could not determine".
   */
  getPathVersionCount(pathGuid: string): Observable<number | null> {
    return this.http.get<any>(`${this.base}/paths/${pathGuid}/versions`).pipe(
      map((r) => {
        const rows = Array.isArray(r) ? r : (r?.items ?? r?.data ?? []);
        const ids = new Set(
          (rows as any[])
            .map((v) => Number(v.version_id ?? v.id))
            .filter((id) => id > 0),
        );
        return ids.size;
      }),
      catchError(() => of(null)),
    );
  }

  getPathVersions(pathGuid: string): Observable<{ version_id: number; name: string }[]> {
    return this.http.get<any>(`${this.base}/paths/${pathGuid}/versions`).pipe(
      map((r) => {
        const rows = Array.isArray(r) ? r : (r?.items ?? r?.data ?? []);
        return (rows as any[])
          .map((v) => ({
            version_id: Number(v.version_id ?? v.id),
            name: String(v.name ?? v.display_name ?? ''),
          }))
          .filter((v) => v.version_id > 0);
      }),
      catchError(() => of([])),
    );
  }

  generateReport(payload: { report_config: unknown; path_ids?: number[] }): Observable<GenerateReportResponse> {
    return this.http.post<GenerateReportResponse>(`${this.base}/reports/generate`, payload);
  }

  private extractItems<T>(r: any): T[] {
    return this.extractListRows<T>(r);
  }

  private extractListRows<T = any>(r: any): T[] {
    if (Array.isArray(r)) return r;
    if (Array.isArray(r?.data)) return r.data;
    if (Array.isArray(r?.items)) return r.items;
    if (Array.isArray(r?.data?.items)) return r.data.items;
    return [];
  }

  private mapMaintenanceRows(r: any): FilterModuleItem[] {
    return this.extractListRows(r)
      .filter((row: any) => row?.module_id)
      .map((row: any) => ({
        module_id: Number(row.module_id),
        name: row.item_details?.name ?? row.module_name ?? row.name ?? `Modul #${row.module_id}`,
        required: Boolean(row.required),
      }))
      .filter((m: FilterModuleItem) => m.module_id > 0);
  }

  private dedupeModules(modules: FilterModuleItem[]): FilterModuleItem[] {
    const byId = new Map<number, FilterModuleItem>();
    for (const m of modules) {
      const existing = byId.get(m.module_id);
      byId.set(m.module_id, existing
        ? { ...existing, required: existing.required || m.required }
        : m);
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'da'));
  }

  private mapUserRows(r: any): FilterUserItem[] {
    const rows = r?.items ?? r?.data ?? [];
    if (!Array.isArray(rows)) return [];
    return rows
      .map((u: any) => ({
        id: Number(u.id ?? u.users_id),
        uuid: u.uuid ?? '',
        first_name: u.first_name ?? '',
        last_name: u.last_name ?? '',
        full_name: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || '',
        email: u.email || u.user || '',
      }))
      .filter((u: FilterUserItem) => Number.isFinite(u.id) && u.id > 0);
  }

  private dedupeUsers(users: FilterUserItem[]): FilterUserItem[] {
    const seen = new Set<number>();
    return users.filter(u => {
      if (seen.has(u.id)) return false;
      seen.add(u.id);
      return true;
    });
  }
}